import React from 'react';

const TanpuraAnimation: React.FC = () => {
  return (
    <div className="relative w-full h-[600px] flex items-center justify-center pointer-events-none select-none">
        {/* SVG Content */}
        <svg 
            viewBox="0 0 500 700" 
            className="w-full h-full drop-shadow-2xl" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid meet"
        >
            <defs>
                 <linearGradient id="bodyGradient" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#FDFBF7" />
                    <stop offset="100%" stopColor="#F0EBE0" />
                 </linearGradient>
            </defs>
            
            <style>{`
                .tanpura-outline {
                    stroke: #6D0F18;
                    stroke-width: 2.5;
                    stroke-linecap: round;
                    stroke-linejoin: round;
                    fill: url(#bodyGradient);
                }
                .tanpura-detail {
                    stroke: #6D0F18;
                    stroke-width: 1;
                    fill: none;
                    opacity: 0.4;
                }
                .wave {
                    stroke: #C5A059;
                    stroke-width: 2;
                    stroke-linecap: round;
                    fill: none;
                    opacity: 0; /* Hidden by default */
                }
                .string {
                    stroke: #6D0F18;
                    stroke-width: 1.5;
                }
                
                /* --- Animations --- */

                /* Gentle floating of the whole instrument */
                @keyframes floatInstrument {
                    0%, 100% { transform: translateY(0) rotate(0deg); }
                    50% { transform: translateY(-15px) rotate(0.5deg); }
                }
                
                /* The Pluck Action */
                /* Simulates: Pull -> Release -> Vibrate -> Rest */
                @keyframes pluckString {
                    0% { transform: translateX(0); }
                    5% { transform: translateX(2px); } /* Pull */
                    10% { transform: translateX(-2px); } /* Release snap */
                    15% { transform: translateX(1.5px); }
                    20% { transform: translateX(-1px); }
                    25% { transform: translateX(0.5px); }
                    30% { transform: translateX(0); }
                    100% { transform: translateX(0); }
                }

                /* Sound waves appearing in sync with plucks */
                @keyframes emitWave {
                    0% { opacity: 0; transform: scale(0.8) translateX(0); stroke-width: 3; }
                    20% { opacity: 0.6; }
                    100% { opacity: 0; transform: scale(1.5) translateX(20px); stroke-width: 0; }
                }

                .floating { animation: floatInstrument 8s ease-in-out infinite; }
                
                /* Sequential Plucking: Cycle duration 4s (Standard Tanpura cycle) */
                /* String 1 (Pa) starts at 0s */
                .pluck-1 { animation: pluckString 4s infinite cubic-bezier(0.4, 0, 0.2, 1); animation-delay: 0s; }
                /* String 2 (Sa) starts at 1s */
                .pluck-2 { animation: pluckString 4s infinite cubic-bezier(0.4, 0, 0.2, 1); animation-delay: 1s; }
                /* String 3 (Sa) starts at 2s */
                .pluck-3 { animation: pluckString 4s infinite cubic-bezier(0.4, 0, 0.2, 1); animation-delay: 2s; }
                /* String 4 (Low Sa) starts at 3s */
                .pluck-4 { animation: pluckString 4s infinite cubic-bezier(0.4, 0, 0.2, 1); animation-delay: 3s; }

                /* Waves synced to strings */
                .wave-emit-1 { animation: emitWave 4s infinite ease-out; animation-delay: 0s; transform-origin: center; }
                .wave-emit-2 { animation: emitWave 4s infinite ease-out; animation-delay: 1s; transform-origin: center; }
                .wave-emit-3 { animation: emitWave 4s infinite ease-out; animation-delay: 2s; transform-origin: center; }
                .wave-emit-4 { animation: emitWave 4s infinite ease-out; animation-delay: 3s; transform-origin: center; }

            `}</style>

            {/* Background Atmosphere - Large flowing waves behind */}
             <path 
                d="M 0 350 C 150 250, 350 450, 500 350" 
                className="stroke-royal-maroon/5 fill-none stroke-[20px]"
             />

            {/* Instrument Group */}
            <g className="floating">
                
                {/* --- Resonance Waves (Synced to plucks) --- */}
                {/* Visualizing the Omkara / Naada */}
                <g transform="translate(250, 350)">
                   <circle r="60" className="wave wave-emit-1" />
                   <circle r="70" className="wave wave-emit-2" />
                   <circle r="80" className="wave wave-emit-3" />
                   <circle r="90" className="wave wave-emit-4" />
                </g>

                {/* --- Instrument Body --- */}
                
                {/* Resonator (Kudam) */}
                <path 
                    d="M 235 150 
                       L 238 420
                       C 238 420, 150 450, 150 540
                       C 150 620, 200 660, 250 660
                       C 300 660, 350 620, 350 540
                       C 350 450, 262 420, 262 420
                       L 265 150
                       Z"
                    className="tanpura-outline shadow-xl"
                />

                {/* Decorative Inlay Lines */}
                <path 
                    d="M 245 425
                       Q 170 450, 170 540
                       Q 170 630, 250 630
                       Q 330 630, 330 540
                       Q 330 450, 255 425"
                    className="tanpura-detail"
                    strokeDasharray="4 4"
                />

                {/* Top/Neck Decoration (Yali) - simplified */}
                <path d="M 235 150 L 235 120 L 265 120 L 265 150 Z" 
                      fill="#6D0F18" stroke="none" />
                <circle cx="250" cy="110" r="15" fill="#6D0F18" />
                <path d="M 250 110 L 250 90" stroke="white" strokeWidth="2" />

                {/* Bridge (Kudirai) - The heart of the sound */}
                <g>
                    <rect x="210" y="560" width="80" height="20" rx="4" fill="#6D0F18" />
                    {/* The Jiva (thread) under strings */}
                    <path d="M 220 560 L 280 560" stroke="#FDFBF7" strokeWidth="1" opacity="0.5" />
                </g>

                {/* --- The Strings (The Soul) --- */}
                {/* We use vector-effect to keep stroke width constant during transform */}
                
                {/* String 1: Pa (Panchamam) */}
                <line x1="242" y1="120" x2="242" y2="580" className="string pluck-1" />
                
                {/* String 2: Sarani (High Sa) */}
                <line x1="247" y1="120" x2="247" y2="580" className="string pluck-2" />
                
                {/* String 3: Anusarani (High Sa) */}
                <line x1="253" y1="120" x2="253" y2="580" className="string pluck-3" />
                
                {/* String 4: Mandram (Low Sa) */}
                <line x1="258" y1="120" x2="258" y2="580" className="string pluck-4" />


                {/* Tuning Pegs (Birudais) */}
                <g fill="#6D0F18">
                    {/* Front facing pegs for a cleaner look */}
                    <circle cx="225" cy="180" r="5" />
                    <rect x="225" y="178" width="15" height="4" fill="#6D0F18" />
                    
                    <circle cx="225" cy="230" r="5" />
                    <rect x="225" y="228" width="15" height="4" fill="#6D0F18" />

                    <circle cx="275" cy="180" r="5" />
                    <rect x="260" y="178" width="15" height="4" fill="#6D0F18" />

                    <circle cx="275" cy="230" r="5" />
                    <rect x="260" y="228" width="15" height="4" fill="#6D0F18" />
                </g>

                {/* Tailpiece */}
                 <path d="M 240 660 L 240 680 Q 250 690, 260 680 L 260 660 Z" fill="#6D0F18" />
            </g>
        </svg>
    </div>
  );
};

export default TanpuraAnimation;